import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, CardContent, Chip, Divider, Stack, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import GlobalWrapper from '../../../components/Wrapper/GlobalWrapper';
import { getCoursePurchaseRequest } from '../../../redux/features/studentPortalSlice';
import { COURSE_PURCHASE_TABLE_HEAD } from '../../../components/datagrid/studentPortal/CoursePurchaseTableHead';
import { Icon } from '@iconify/react/dist/iconify.js';
import AssignCro from './AssignCRO';
import ActiveCourses from './ActiveCourses';
import RequestRejection from './RejectRequest';
import ToolTip from '../../../components/ToolTip/ToolTip';
import { useNavigate } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';

const CoursePurchaseRequest = ({ cro }) => {
  const dispatch = useDispatch();
  const { coursePurchase, loading , purchaseData } = useSelector((state) => state.studentPortal);
  const [selectedRow, setSelectedRow] = useState(null);
  const [assignopen, setAssignOpen] = useState(false);
  const [courseopen, setCourseOpen] = useState(false);
  const [rejectOpen, setrejectOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getCoursePurchaseRequest());
  }, [dispatch]);

  const isCRM = purchaseData?.isReportingHead === true;

  const handleNavigate = (params) => {
    navigate(`/my-workspace/cart`, {
      state: params,
    });
  };

  const columns = useMemo(
    () => [
      ...COURSE_PURCHASE_TABLE_HEAD,
      {
        field: 'cart',
        headerName: 'Go to Cart',
        width: 180,
        renderCell: (params) => {
          return (
            <Button
              size="small"
              onClick={() => handleNavigate(params.row)}
              disabled={params.row.status === 3}
              sx={{
                color: 'white',
                background: `linear-gradient(135deg, #0089d0, #a125c2)`,
                borderRadius: 3,
                px: 1.5,
              }}
            >
              <Icon icon="mdi:cart-outline" height={22} width={22} />
              Pick My Courses
            </Button>
          );
        },
      },
      {
        field: 'contact',
        headerName: 'Whatsapp Contact',
        width: 200,
        renderCell: (params) => {
          const phone = `${params.row.country_code}${params.row.mob_no}`;

          const message = `Hello ${params.row.student_name}, I am contacting from Interval Learning regarding your ${params.row.course_name} course purchase request.`;

          const handleWhatsapp = () => {
            const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
            window.open(url, '_blank');
          };

          return (
            <Button
              size="small"
              onClick={handleWhatsapp}
              disabled={params.row.status === 3}
              startIcon={<Icon icon="ic:baseline-whatsapp" width={18} />}
              sx={{
                background: 'linear-gradient(135deg,#25D366,#128C7E)',
                color: '#fff',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 3,
                px: 2,
                '&:hover': {
                  background: 'linear-gradient(135deg,#1ebe5d,#0f7a6c)',
                },
              }}
            >
              WhatsApp
            </Button>
          );
        },
      },
      ...(isCRM
        ? [
            {
              field: 'action',
              headerName: 'Assign CRO',
              width: 150,
              renderCell: (params) => (
                <Button
                  size="small"
                  sx={{
                    background: `linear-gradient(135deg, #0089d0, #a125c2)`,
                    borderRadius: 3,
                    color: '#fff',
                    px: 1.5,
                  }}
                  disabled={params.row.status === 3}
                  startIcon={<Icon icon={'material-symbols:person-rounded'} width={18} height={18} />}
                  onClick={() => {
                    setSelectedRow(params.row);
                    setAssignOpen(true);
                  }}
                >
                  Assign CRO
                </Button>
              ),
            },
          ]
        : []),
      {
        field: 'reject',
        headerName: 'Reject Request',
        width: 170,
        renderCell: (params) => (
          <Button
            size="small"
            variant="outlined"
            disabled={params.row.status === 3}
            color="error"
            startIcon={<Icon icon={'gridicons:cross-circle'} width={18} height={18} />}
            onClick={() => {
              setSelectedRow(params.row);
              setrejectOpen(true);
            }}
          >
            Reject Request
          </Button>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 150,
        renderCell: (params) => {
          if (params.row.status === 3) {
            return <Chip label="Rejected" variant="outlined" color="error" />;
          } else return 'N/A';
        },
      },
      {
        field: 'remarks',
        headerName: 'Rejection Remarks',
        width: 200,
        renderCell: (params) =>
          params.row.remarks === null ? (
            'N/A'
          ) : (
            <ToolTip content={params.row.remarks} expand={params.row.remarks} />
          ),
      },
      {
        field: 'active_courses',
        headerName: 'Active Courses',
        width: 170,
        renderCell: (params) => (
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<Icon icon={'mdi:book-open-variant'} width={18} height={18} />}
            onClick={() => {
              setSelectedRow(params.row);
              setCourseOpen(true);
            }}
          >
            Active Courses
          </Button>
        ),
      },
    ],
    [COURSE_PURCHASE_TABLE_HEAD, isCRM],
  );

  return (
    <GlobalWrapper title={'Course Purchase Requests'}>
      <Box sx={{ width: '100%', padding: 0 }}>
        <DataGrid
        rows={coursePurchase || []}
        columns={columns}
        loading={loading}
        autoHeight
        getRowId={(row) => row.serialNumber} // ✅ FIX
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 5,
              page:0,
            },
          },
        }}
        pageSizeOptions={[5,10,20]}
        disableRowSelectionOnClick
      />

        {assignopen && selectedRow && (
          <AssignCro rowData={selectedRow} open={assignopen} setOpen={setAssignOpen}/>
        )}
        {courseopen && selectedRow && (
          <ActiveCourses rowData={selectedRow} open={courseopen} setOpen={setCourseOpen}/>
        )}
        {rejectOpen && selectedRow && (
          <RequestRejection rowData={selectedRow} setOpen={setrejectOpen} open={rejectOpen} />
        )}
      </Box>
    </GlobalWrapper>
  );
};

export default CoursePurchaseRequest;
